import React, { useState } from 'react';
import cn from 'clsx';
import { Layout } from 'fronton-react';
import { ChevronCircleRightIcon } from '@fronton/icons-react';
import { Divider, Link, RootLayout, SectionTitle, Typography } from 'common/components';
import { SITEMAP_STORE_NAME, SitemapNode, SitemapStructure } from 'modules/sitemap';
import { buildSitemapTree } from 'modules/sitemap/sitemap-utils';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { createElementKey } from 'utils';
import { SITEMAP_PATH, TOP_LEVEL_NAME_MAP } from './sitemap-constants';
import {
  sitemapDivider,
  sitemapExpandLevel,
  sitemapNestedLevel,
  sitemapHideChildren,
  sitemapChevronIcon,
  sitemapRow,
  sitemapOpenChevronIcon,
  sitemapRootNode,
  sitemapRootNodeChildren,
  sitemapRootNodeContainer,
  sitemapSecondNode,
  sitemapTitle,
} from './sitemap-styles';

type NodeProps = {
  treeNode: SitemapNode;
}

const RootNode: React.FC<NodeProps> = ({ treeNode }) => (
  <div className={sitemapRootNodeContainer}>
    <Link
      className={sitemapRootNode}
      href={treeNode.pageUrl}
      title={treeNode.name}
    >
      <Typography variant="h3">{TOP_LEVEL_NAME_MAP[treeNode.pageUrl] || treeNode.name}</Typography>
    </Link>
    <Divider className={sitemapDivider} />
    <div className={sitemapRootNodeChildren}>
      {treeNode.children.length > 0 && <SitemapTree sitemapTree={treeNode.children} />}
    </div>
  </div>
);

const SecondNode: React.FC<NodeProps> = ({ treeNode }) => (
  <div>
    <Link
      className={sitemapSecondNode}
      href={treeNode.pageUrl}
      title={treeNode.name}
    >
      <Typography variant="m" size="body_accent">{treeNode.name}</Typography>
    </Link>
    {treeNode.children.length > 0 && <SitemapTree sitemapTree={treeNode.children} />}
  </div>
);

const ExpandableNode: React.FC<NodeProps> = ({ treeNode }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleExpandRowClick = () => {
    setIsOpen(!isOpen);
  }

  return (
    <div className={cn({ [sitemapNestedLevel]: treeNode.level > 3 })}>
      <div className={sitemapExpandLevel}>
        <ChevronCircleRightIcon
          className={cn(sitemapChevronIcon, { [sitemapOpenChevronIcon]: isOpen })}
          onClick={handleExpandRowClick}
          type={isOpen ? 'fill' : 'outline'}
          size='m'
          color='text-primary'
        />
        <Link
          href={treeNode.pageUrl}
          title={treeNode.name}
        >
          <Typography variant="s" size="body_long">{treeNode.name}</Typography>
        </Link>
      </div>
      <div className={cn({[sitemapHideChildren]: !isOpen})}>
        {treeNode.children.length > 0 && <SitemapTree sitemapTree={treeNode.children} />}
      </div>
    </div>
  );
}

const TreeNode: React.FC<NodeProps> = ({ treeNode }) => (
  <div className={cn(sitemapRow, { [sitemapNestedLevel]: treeNode.level > 3 })}>
    <Link
      href={treeNode.pageUrl}
      title={treeNode.name}
    >
      <Typography variant="s" size="body_long">{treeNode.name}</Typography>
    </Link>
    {treeNode.children.length > 0 && <SitemapTree sitemapTree={treeNode.children} />}
  </div>
);

const SitemapTree: React.FC<{ sitemapTree: SitemapStructure }> = ({ sitemapTree }) => (
  sitemapTree.map((node, index) => {
    if (node.level === 1) {
      return <RootNode key={createElementKey(`sitemap-level-${node.level}`, index)} treeNode={node} />
    }

    if (node.level === 2) {
      return <SecondNode key={createElementKey(`sitemap-level-${node.level}`, index)} treeNode={node} />
    }

    if (node.children.length > 0) {
      return <ExpandableNode key={createElementKey(`sitemap-level-${node.level}`, index)} treeNode={node} />
    }

    return (
      <TreeNode key={createElementKey(`sitemap-level-${node.level}`, index)} treeNode={node} />
    )
  })
);

/**
 * Вью страницы Карта сайта
 */
export const Sitemap = () => {
  const { sitemap } = useStoreon<State>(SITEMAP_STORE_NAME);
  const sitemapTree = buildSitemapTree(sitemap.pageList || []);

  return (
    <RootLayout currentPage={SITEMAP_PATH}>
      <Layout>
        <SectionTitle as="h1" className={sitemapTitle}>Карта сайта</SectionTitle>
        <SitemapTree sitemapTree={sitemapTree} />
      </Layout>
    </RootLayout>
  );
};
